import React from 'react';

/**
 * Tile gradient palette shared by both `MetricTile` and `CompactMetricTile`.
 * Adding new accents here automatically makes them available in both tile types.
 */
export const tileAccentMap = {
  sky:     'from-sky-500 to-indigo-500',
  violet:  'from-violet-500 to-fuchsia-500',
  rose:    'from-rose-500 to-pink-500',
  amber:   'from-amber-500 to-orange-500',
  emerald: 'from-emerald-500 to-teal-500',
  indigo:  'from-indigo-500 to-blue-500',
  fuchsia: 'from-fuchsia-500 to-pink-500',
  teal:    'from-teal-500 to-cyan-500',
};

/**
 * Large, decorative read-only KPI tile.
 * Currently used by `ReconciliationTab.js` and any other surface that wants
 * the original Service-Reports tile look. Not used on the new Job-Type
 * metric row (see `CompactMetricTile`).
 */
export const MetricTile = ({ label, value, accent = 'sky' }) => (
  <div className="relative rounded-2xl bg-white/75 backdrop-blur border border-white/50 p-4 overflow-hidden shadow-[0_8px_30px_rgba(15,23,42,0.04)]">
    <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-15 blur-2xl bg-gradient-to-br ${tileAccentMap[accent] || tileAccentMap.sky}`} />
    <p className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
    <p className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{value}</p>
  </div>
);

/**
 * Compact, click-to-filter tile used in the Job-Type metrics row at the top of
 * `/service/reports`. Sized so 8 tiles fit in 2 rows on a typical desktop and 6
 * tiles fit in 2 rows on tablet. The active filter highlights with a colored
 * border + tinted background.
 */
export const CompactMetricTile = ({ label, value, accent = 'sky', active, onClick, testId }) => {
  const cls = tileAccentMap[accent] || tileAccentMap.sky;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={`Filter by ${label}`}
      className={`relative rounded-xl p-2 text-left overflow-hidden transition-all duration-150 border ${
        active
          ? 'bg-white shadow-[0_4px_14px_rgba(99,102,241,0.18)] border-indigo-300 ring-1 ring-indigo-300'
          : 'bg-white/70 hover:bg-white border-white/60 hover:border-slate-200 shadow-sm hover:shadow'
      }`}
    >
      <div className={`absolute -top-6 -right-6 w-16 h-16 rounded-full opacity-15 blur-xl bg-gradient-to-br ${cls}`} />
      <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-medium leading-tight truncate" title={label}>
        {label}
      </p>
      <p className="text-[18px] font-semibold tracking-tight text-slate-900 leading-tight mt-0.5">{value}</p>
    </button>
  );
};
